import React, { useState } from "react";
import { toast } from "sonner";
import { Trophy } from "lucide-react";
import { BAND, bigBtn, gcard } from "./gedKit";

/** Shared by the PACT Life Pass and the staff overview: the four subjects, strongest first, colour-coded by GED Ready band. */
export function SubjectCards({ subjects, focus, light }) {
  return (
    <div className="space-y-2" data-testid="ged-subjects">
      {subjects.map((s, i) => {
        const b = BAND[s.band];
        return (
          <div key={s.key} data-testid={`ged-subject-${s.key}`} className={`rounded-2xl border-2 p-4 ${b ? b.cls : light ? "border-white/10" : "border-white/15 bg-white/[0.03]"}`}>
            <div className="flex items-center gap-3">
              <span className="h-8 w-8 shrink-0 grid place-items-center rounded-full bg-black/30 font-bold">{i + 1}</span>
              <span className="flex-1 font-semibold text-base">{s.name}</span>
              <span data-testid={`ged-score-${s.key}`} className="text-2xl font-bold">{s.score ?? "—"}</span>
            </div>
            <div className="mt-2 flex items-center gap-2 text-sm">
              {s.key === focus && <span data-testid="ged-focus-badge" className="inline-flex items-center gap-1 rounded-full bg-[#d4a017] text-black px-2.5 py-0.5 font-semibold"><Trophy className="h-3.5 w-3.5" />Focus first</span>}
              <span className="opacity-80">{b ? b.label : "No score yet"}{s.attempts > 1 ? ` · ${s.attempts} tests` : ""}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ScoreForm({ subjects, onSave, testid = "ged-score-form" }) {
  const [subject, setSubject] = useState(subjects[0]?.key || "math");
  const [score, setScore] = useState("");
  const [taken, setTaken] = useState(new Date().toLocaleDateString("en-CA"));
  const [busy, setBusy] = useState(false);
  const n = Number(score);
  const ok = Number.isInteger(n) && n >= 100 && n <= 200;
  const save = async () => {
    setBusy(true);
    try { await onSave({ subject, score: n, taken_on: taken }); setScore(""); toast.success("Score saved"); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    setBusy(false);
  };
  const field = "w-full min-h-[52px] rounded-xl bg-black/40 border-2 border-white/20 px-3 text-base text-white [color-scheme:dark] outline-none focus:border-[#d4a017]";
  return (
    <div className={`${gcard} space-y-3`} data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.25em] text-[#d4a017]">Add a GED Ready score</div>
      <select value={subject} onChange={(e) => setSubject(e.target.value)} className={field} data-testid={`${testid}-subject`}>
        {[...subjects].sort((a, b) => a.name.localeCompare(b.name)).map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
      </select>
      <div className="grid grid-cols-2 gap-2">
        <input inputMode="numeric" placeholder="Score 100–200" value={score} onChange={(e) => setScore(e.target.value.replace(/\D/g, ""))} className={field} data-testid={`${testid}-score`} />
        <input type="date" max={new Date().toLocaleDateString("en-CA")} value={taken} onChange={(e) => setTaken(e.target.value)} className={field} data-testid={`${testid}-date`} />
      </div>
      <button type="button" onClick={save} disabled={!ok || busy} className={bigBtn} data-testid={`${testid}-save`}>{busy ? "Saving…" : "Save score"}</button>
      <div className="flex flex-wrap gap-2 text-xs"><span className="rounded-full bg-red-500/20 px-2 py-1">100–133 Review</span><span className="rounded-full bg-amber-400/20 px-2 py-1">134–144 Close</span><span className="rounded-full bg-emerald-500/20 px-2 py-1">145–200 Likely pass</span></div>
    </div>
  );
}

export default function Baseline({ d, api, voice, onChange }) {
  const save = async (body) => { const s = await api.post("/portal/resident/ged/scores", body); onChange(s); voice.play("right"); };
  return (
    <div className="space-y-4" data-testid="ged-baseline">
      <p className="text-base text-white/70">Your strongest subject comes first, so you start with a quick win.</p>
      <SubjectCards subjects={d.subjects} focus={d.focus} />
      <ScoreForm subjects={d.subjects} onSave={save} />
    </div>
  );
}
